import React, { useState, useEffect } from 'react';
import { Card, Spin, Alert, Select, Row, Col, Statistic } from 'antd';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line
} from 'recharts';
import { instance } from "../../../API/API";

const { Option } = Select;

const MonthlyReportsChart = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chartType, setChartType] = useState('bar');

    useEffect(() => {
        fetchMonthlyData();
    }, []);

    const fetchMonthlyData = async () => {
        try {
            setLoading(true);
            const response = await instance.get('analytics/monthly_reports_completion.php');

            if (response.data.success) {
                setData(response.data.data);
            } else {
                throw new Error('Invalid response format');
            }
        } catch (err) {
            console.error('Error fetching monthly reports data:', err);
            setError('Ошибка загрузки данных о загруженных отчетах');
        } finally {
            setLoading(false);
        }
    };

    // Фильтруем данные только до текущего месяца
    const currentMonth = new Date().getMonth() + 1; // JavaScript месяцы с 0 до 11
    const monthNames = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
    const filteredData = data.filter((item, index) => index < currentMonth);

    // Расчет общей статистики только за актуальные месяцы
    const totalStats = filteredData.reduce((acc, item) => ({
        expected: acc.expected + item.expected,
        actual: acc.actual + item.actual,
        plans: acc.plans + item.total_plans,
        brigades: acc.brigades + item.total_brigades
    }), { expected: 0, actual: 0, plans: 0, brigades: 0 });

    const overallCompletionRate = totalStats.expected > 0
        ? Math.round((totalStats.actual / totalStats.expected) * 100)
        : 0;

    if (loading) {
        return (
            <Card style={{
                width: '100%',
                height: '500px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Spin size="large" />
            </Card>
        );
    }

    if (error) {
        return (
            <Card style={{ width: '100%', height: '500px' }}>
                <Alert message={error} type="error" />
            </Card>
        );
    }

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                }}>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
                    <p style={{ margin: 0, color: '#8884d8' }}>
                        Ожидаемые отчеты: {payload[0].value}
                    </p>
                    <p style={{ margin: 0, color: '#82ca9d' }}>
                        Фактические отчеты: {payload[1].value}
                    </p>
                    <p style={{ margin: 0, color: '#ff7300' }}>
                        Выполнение: {payload[2]?.value || 0}%
                    </p>
                    {payload[3] && (
                        <p style={{ margin: 0, color: '#8c8c8c' }}>
                            Планов работ: {payload[3].value}
                        </p>
                    )}
                </div>
            );
        }
        return null;
    };

    return (
        <div style={{ width: '100%' }}>
            <Card
                title={`Статистика загруженных отчетов по месяцам (${new Date().getFullYear()} год)`}
                extra={
                    <Select
                        value={chartType}
                        onChange={setChartType}
                        style={{ width: 120 }}
                    >
                        <Option value="bar">Столбцы</Option>
                        <Option value="line">Линии</Option>
                    </Select>
                }
                style={{
                    width: '100%',
                    background: 'white',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
                bodyStyle={{
                    padding: '16px',
                    width: '100%'
                }}
            >
                {/* Статистика */}
                <Row gutter={16} style={{ marginBottom: 20, width: '100%' }}>
                    <Col span={6}>
                        <Statistic
                            title="Всего планов работ"
                            value={totalStats.plans}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Всего бригад"
                            value={totalStats.brigades}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Ожидаемые отчеты"
                            value={totalStats.expected}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Фактические отчеты"
                            value={totalStats.actual}
                            valueStyle={{ color: totalStats.actual >= totalStats.expected ? '#3f8600' : '#cf1322' }}
                        />
                    </Col>
                </Row>

                {/* График */}
                <div style={{ width: '100%', height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {chartType === 'bar' ? (
                            <BarChart
                                data={filteredData}
                                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="month"
                                />
                                <YAxis yAxisId="left" />
                                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar
                                    yAxisId="left"
                                    dataKey="expected"
                                    name="Ожидаемые отчеты"
                                    fill="#8884d8"
                                />
                                <Bar
                                    yAxisId="left"
                                    dataKey="actual"
                                    name="Фактические отчеты"
                                    fill="#82ca9d"
                                />
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="completion_rate"
                                    name="% выполнения"
                                    stroke="#ff7300"
                                    strokeWidth={2}
                                    dot={{ fill: '#ff7300', strokeWidth: 2 }}
                                />
                            </BarChart>
                        ) : (
                            <LineChart
                                data={filteredData}
                                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="month"
                                />
                                <YAxis />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="expected"
                                    name="Ожидаемые отчеты"
                                    stroke="#8884d8"
                                    strokeWidth={2}
                                    dot={{ fill: '#8884d8', strokeWidth: 2 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="actual"
                                    name="Фактические отчеты"
                                    stroke="#82ca9d"
                                    strokeWidth={2}
                                    dot={{ fill: '#82ca9d', strokeWidth: 2 }}
                                />
                            </LineChart>
                        )}
                    </ResponsiveContainer>
                </div>

                {/* Общий процент выполнения */}
                <div style={{
                    marginTop: 20,
                    padding: '10px 0',
                    textAlign: 'center',
                    borderTop: '1px solid #f0f0f0',
                    width: '100%'
                }}>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                        Общий процент выполнения: {' '}
                        <span style={{
                            color: overallCompletionRate >= 80 ? '#3f8600' :
                                overallCompletionRate >= 60 ? '#faad14' : '#cf1322',
                            fontWeight: 'bold'
                        }}>
                            {overallCompletionRate}%
                        </span>
                    </span>
                </div>
            </Card>
        </div>
    );
};

export default MonthlyReportsChart;